// Top-level React error boundary. Catches render-time crashes anywhere
// in the tree (anything outside the rAF loop, which has its own try/catch
// in GameCanvas.jsx) and forwards them to the same /api/_client-error
// telemetry feed. Renders a graceful fallback so the entire app doesn't
// blank out — the pilot can hit "Reload" or navigate via the Navbar.
//
// Why a class component? React's error boundary contract requires
// componentDidCatch + getDerivedStateFromError, which only exist on
// class components. The rest of the app is functional.
import { Component } from "react";
import api from "../lib/api";

export class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    // One-shot latch so repeated re-renders don't flood telemetry.
    this._reported = false;
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    if (this._reported) return;
    this._reported = true;
    // Fire-and-forget — telemetry endpoint dedupes per-user/surface
    // within 30s on the server side too.
    try {
      api.post("/_client-error", {
        message: String(error?.message || error).slice(0, 500),
        stack: String(error?.stack || info?.componentStack || "").slice(0, 4000),
        surface: "react-tree",
        url: typeof window !== "undefined" ? window.location.pathname : "",
        user_agent: typeof navigator !== "undefined" ? (navigator.userAgent || "").slice(0, 200) : "",
      }).catch(() => {});
    } catch { /* never throw out of the error boundary */ }
  }

  render() {
    if (this.state.error) {
      return (
        <div
          className="min-h-screen bg-[#05050a] text-white flex items-center justify-center p-6"
          data-testid="app-error-boundary"
        >
          <div className="max-w-md w-full border border-[#ff007f]/40 bg-black/70 p-6 flex flex-col gap-3">
            <div className="font-heading uppercase text-2xl tracking-tight text-[#ff007f]">
              Something Broke
            </div>
            <div className="font-mono-cosmic text-[10px] tracking-[0.2em] uppercase text-white/45">
              The error has been reported automatically.
            </div>
            <div className="text-white/65 text-sm leading-snug">
              We hit an unexpected error rendering this page. Try a reload or
              head back home — the rest of the app should still work.
            </div>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-4 py-2 border border-[#00ff41] text-[#00ff41] font-mono-cosmic text-[11px] tracking-[0.25em] uppercase hover:bg-[#00ff41]/10 transition"
                data-testid="error-boundary-reload"
              >
                Reload
              </button>
              <button
                type="button"
                onClick={() => { window.location.href = "/"; }}
                className="px-4 py-2 border border-white/30 text-white/70 font-mono-cosmic text-[11px] tracking-[0.25em] uppercase hover:bg-white/5 transition"
                data-testid="error-boundary-home"
              >
                Home
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
